import { useApiMutation } from "@gladis/api-client";
import type { HouseholdDto } from "@gladis/shared";
import { useState, type ReactNode } from "react";
import { Button, Label, TextField } from "@gladis/ui";
import { tRich, errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Renaming a household (FR-021). Owner or Admin.
 *
 * Trivial on its own, and kept beside deletion on purpose: they are the two things you can do to
 * the household record itself, and putting the harmless one first gives the destructive one some
 * context to sit in.
 */
export function RenameHouseholdForm({ household }: { household: HouseholdDto }): ReactNode {
  const t = useT();
  const [name, setName] = useState(household.name);

  const rename = useApiMutation<HouseholdDto, { name: string }>(
    (client, body) => client.patch(`/households/${household.id}`, body),
    { invalidates: [["me"]] },
  );

  const trimmed = name.trim();
  const unchanged = trimmed === household.name;

  return (
    <section className="mt-10 rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.household.name")}</h2>

      <form
        className="mt-3"
        onSubmit={(event) => {
          event.preventDefault();
          rename.mutate({ name: trimmed });
        }}
      >
        {/* "Household name", not "Name": ProfileSettings on this same page has a Display name
            field with its own Save, and two controls whose accessible names differ only by context
            are ambiguous to anyone navigating by label rather than by eye. */}
        <Label htmlFor="household-name">{t("identity.household.name")}</Label>
        <TextField
          id="household-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <Button
          className="mt-4"
          type="submit"
          // Empty and whitespace-only are both rejected by the server too (FR-021) — the trim here
          // is so the button reflects what will actually be sent, not so it can be the only check.
          disabled={trimmed === "" || unchanged || rename.isPending}
        >
          {rename.isPending ? t("common.saving") : t("identity.household.saveName")}
        </Button>
      </form>

      {rename.error ? (
        <p className="mt-3 text-sm text-muted" role="alert">
          {errorSentence(rename.error)}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Deleting a household (AC-H8).
 *
 * ============================================================================
 * THE ONLY IRREVERSIBLE ACTION IN GLADIS. IT IS DELIBERATELY AWKWARD.
 * ============================================================================
 *
 * Typing the household's name is not friction for its own sake: it is the difference between "I
 * clicked the wrong red button" and "I typed the name of the thing I meant to destroy". The
 * comparison is exact — not trimmed, not case-folded — here AND in `canDeleteHousehold`, and the
 * server's copy is the one that counts. A confirm button that stays disabled is a courtesy; the
 * rule lives on the server.
 *
 * NOT RENDERED FOR AN ADMIN. `HouseholdSettings` omits it rather than disabling it: a disabled
 * control advertises a capability they will never have in this household, and invites them to go
 * looking for how to earn it.
 */
export function DeleteHouseholdDialog({
  household,
  onDeleted,
}: {
  household: HouseholdDto;
  onDeleted: () => void;
}): ReactNode {
  const t = useT();
  const [confirmName, setConfirmName] = useState("");
  const [open, setOpen] = useState(false);

  const remove = useApiMutation<void, { confirmName: string }>(
    (client, body) => client.delete(`/households/${household.id}`, body),
    { discardsEverything: true, onSuccess: onDeleted },
  );

  return (
    <section className="mt-10 rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.household.deleteHeading")}</h2>

      {open ? (
        <div className="mt-3" role="alert">
          <p>
            {tRich("identity.household.deleteEverything", {
              household: <strong>{household.name}</strong>,
            })}
          </p>
          <p className="mt-2 text-sm text-muted">{t("identity.household.deleteUnaffected")}</p>

          <div className="mt-4">
            <Label htmlFor="confirm-household-name">
              {tRich("identity.household.typeToConfirm", {
                household: <strong>{household.name}</strong>,
              })}
            </Label>
            <TextField
              id="confirm-household-name"
              value={confirmName}
              autoComplete="off"
              onChange={(event) => setConfirmName(event.target.value)}
            />
          </div>

          <div className="mt-4 flex gap-3">
            <Button
              variant="primary"
              type="button"
              disabled={confirmName !== household.name || remove.isPending}
              onClick={() => remove.mutate({ confirmName })}
            >
              {remove.isPending ? t("common.deleting") : t("identity.household.deleteConfirm")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpen(false);
                setConfirmName("");
              }}
            >
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-2 text-muted">{t("identity.household.deleteWarning")}</p>
          <Button className="mt-4" type="button" onClick={() => setOpen(true)}>
            {t("identity.household.deleteButton")}
          </Button>
        </>
      )}

      {remove.error ? (
        <p className="mt-3 text-sm text-muted" role="alert">
          {errorSentence(remove.error)}
        </p>
      ) : null}
    </section>
  );
}
